import { styled } from '@mui/material';
import { ReactElement, useEffect } from 'react';

import { Layout } from '@localstack/types';

import { useNavigation } from '@localstack/ui';

import { NewsBanner } from './components';

const LayoutContainer = styled('div')(({ theme }) => ({
  background: theme.palette.background.default,
  display: 'flex',
  height: '100vh',
}));

const PageContainer = styled('div')(() => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
}));

const PageContent = styled('div')(() => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
}));

interface NavigationProps {
  isExpanded?: boolean;
}

const Navigation = styled('div')<NavigationProps>(({ theme, isExpanded }) => ({
  flexGrow: 0,
  flexShrink: 0,
  borderColor: `${theme.palette.divider} !important`,
  borderRight: '1px solid',
  backgroundColor: theme.palette.navigation.background,
  overflowY: 'auto',
  width: 'auto',
  overflow: 'auto',

  ...(isExpanded && {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1000,
    width: 'auto',
    overflow: 'auto',
  }),
}));

interface NavigationWrapperProps {
  navigation: React.ReactNode;
}

const NavigationWrapper = ({ navigation }: NavigationWrapperProps): JSX.Element => {
  const { isNavOpen, isMobile } = useNavigation();
  const isExpanded = isMobile && isNavOpen;

  return (
    <Navigation data-name="navigation" isExpanded={isExpanded}>
      {(!isMobile || isNavOpen) && navigation}
    </Navigation>
  );
};

export const BaseLayout = ({
  pageName,
  children,
  title,
  documentTitle,
  navigation,
  hideNavigation,
}: Layout): ReactElement => {
  useEffect(() => {
    if (documentTitle) {
      document.title = `${documentTitle} - LocalStack`;
    } else if (title && typeof title === 'string') {
      document.title = `${title} - LocalStack`;
    } else {
      document.title = 'LocalStack';
    }
  }, [title, documentTitle]);

  return (
    <LayoutContainer data-page-name={pageName} data-name="page container">
      {!hideNavigation && <NavigationWrapper navigation={navigation} />}

      <PageContainer data-name="page content">
        {!hideNavigation && <NewsBanner />}
        <PageContent data-name="main content">{children}</PageContent>
      </PageContainer>
    </LayoutContainer>
  );
};
